import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import { PluginContext } from 'ts-components/Plugins/context';
import { Userpilot } from 'userpilot';
import { useLocation } from 'react-router-dom';
import { getUserpilotAppIds, haveSameUserpilotAppIds } from './helpers';

const sentryDSN = import.meta.env.VITE_SENTRY_DSN;
const sentryTraceSample = import.meta.env.VITE_SENTRY_TRACESAMPLE;
const sentryENV = import.meta.env.VITE_SENTRY_ENV;
const sentryRelease = import.meta.env.VITE_SENTRY_RELEASE;
const userpilotAppIdEnv = import.meta.env.VITE_USERPILOT_APP_ID;

const isProduction = import.meta.env.MODE === 'production';
const canEnableSentry = isProduction;

// Users with these emails will be ignored and tracking will not be instrumented. This should not affect monitoring
// because that is useful for all users.
const emailExclusions = ['@kizen-e2e.testinator.com', '+e2e@kizen.com'];

const NETWORK_ERROR_CODES = ['ERR_NETWORK', 'ERR_INTERNET_DISCONNECTED'];
const ABORT_ERROR_CODES = ['ECONNABORTED', 'ERR_CANCELED'];

if (canEnableSentry) {
  const config = {
    dsn: sentryDSN,
    tracesSampleRate: sentryTraceSample,
    environment: sentryENV,
    release: sentryRelease,
    enableAutoSessionTracking: false,
    autoSessionTracking: false,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
    beforeSend: (event, hint) => {
      const exception = hint?.originalException;

      if (!exception || exception.preserveFingerprint) {
        return event;
      }

      const isStatusException =
        exception.status && exception.status >= 400 && exception.status <= 404;
      const isCodeException =
        exception.code && exception.code >= 400 && exception.code <= 404;

      const statusCode = isStatusException
        ? exception.status
        : isCodeException
          ? exception.code
          : null;

      const isGenericNetworkError =
        NETWORK_ERROR_CODES.includes(exception.code) ||
        exception.message === 'Network Error';

      const isCanceledError = ABORT_ERROR_CODES.includes(exception.code);

      if (isStatusException || isCodeException) {
        event.fingerprint = [`http-${statusCode}-error`];
      } else if (isGenericNetworkError) {
        event.fingerprint = ['http-network-error'];
      } else if (isCanceledError) {
        event.fingerprint = ['http-aborted'];
      }

      return event;
    },
  };

  Sentry.init(config);
}

export const InstrumentationProvider = () => {
  const [hasInitializedUserpilot, setHasInitializedUserpilot] = useState(false);
  const initializedUserpilotIds = useRef([]);
  const teamMember = useSelector((s) => s.authentication.teamMember);
  const business = useSelector((s) => s.authentication.chosenBusiness);
  const { isLoading: isLoadingPlugins, plugins } = useContext(PluginContext);
  const location = useLocation();
  const { pathname } = location;

  // Instrumenting user tracking requires user auth information as well as some plugin
  // information, and should not be done in testing environments.
  const canInstrumentUserTracking = teamMember && business && !isLoadingPlugins;

  // Instrumenting monitoring only requires auth information and can be done without
  // waiting for other checks, and should run in testing environments.
  const canInstrumentMonitoring = teamMember && business;

  useEffect(() => {
    if (canEnableSentry && canInstrumentMonitoring) {
      const { email, user: id, full_name: username } = teamMember;
      Sentry.setUser({
        email,
        id,
        username,
        ip_address: '{{auto}}',
        business_name: business.name,
        business_id: business.id,
      });
      Sentry.setTag('kzn-business', business.name);
    }
  }, [teamMember, business, canInstrumentMonitoring]);

  const userpilotBusinessConfig = plugins?.userpilot?.business_config;

  const userpilotAppIds = useMemo(() => {
    return getUserpilotAppIds(userpilotBusinessConfig, userpilotAppIdEnv);
  }, [userpilotBusinessConfig]);

  const hasUserpilotAppIds = userpilotAppIds.length > 0;

  const canEnableUserpilot = hasUserpilotAppIds && hasInitializedUserpilot;

  // Only initialize userpilot once. We'd do this outside the render if we didn't
  // have to wait for the plugins to load to get the appId
  useEffect(() => {
    if (
      initializedUserpilotIds.current.length ||
      !hasUserpilotAppIds ||
      !canInstrumentUserTracking
    ) {
      return;
    }

    initializedUserpilotIds.current = userpilotAppIds;

    userpilotAppIds.forEach((id) => {
      Userpilot.initialize(id, { distro: '2.0' });
    });

    setHasInitializedUserpilot(true);
  }, [hasUserpilotAppIds, canInstrumentUserTracking, userpilotAppIds]);

  const businessId = business?.id;

  // If the userpilot app ID from either slot changes, we need to reload to get a new
  // Userpilot session
  useEffect(() => {
    if (
      hasInitializedUserpilot &&
      window.userpilotSettings?.token &&
      !haveSameUserpilotAppIds(
        userpilotAppIds,
        initializedUserpilotIds.current
      ) &&
      pathname !== '/choose-business'
    ) {
      window.location.reload();
    }
  }, [userpilotAppIds, hasInitializedUserpilot, pathname]);

  useEffect(() => {
    if (canInstrumentUserTracking) {
      const {
        email,
        full_name: username,
        id: teamMemberId,
        created: teamMemberCreated,
      } = teamMember;

      const {
        id: businessId,
        company_name,
        name: businessName,
        is_trial,
        created: businessCreated,
        parent_business,
      } = business;

      const isIgnoredEmail = emailExclusions.some((exclusion) =>
        email.endsWith(exclusion)
      );

      if (isIgnoredEmail) {
        return;
      }

      if (canEnableUserpilot) {
        Userpilot.identify(teamMemberId, {
          name: username,
          email: email,
          created_at: teamMemberCreated,
          company: {
            id: businessId,
            name: `${company_name} - ${businessName}`,
            paying: !is_trial,
            created_at: businessCreated,
            parent_business_id: parent_business,
          },
        });
      }
    }
  }, [
    teamMember,
    business,
    isLoadingPlugins,
    canInstrumentUserTracking,
    canEnableUserpilot,
  ]);

  // Userpilot needs to be reloaded on every route change
  useEffect(() => {
    if (canInstrumentUserTracking && canEnableUserpilot) {
      Userpilot.reload();
    }
  }, [pathname, canInstrumentUserTracking, canEnableUserpilot, businessId]);

  return null;
};
